import { DIRECTIONS } from '@btime/constants'
import {
  State,
  appendValue,
  appendValueOnIndex,
  duplicateValueWithIndex,
  moveBack,
  moveTo,
  moveToPath,
  moveToRoot,
  removeValueWith,
  removeValueWithIndex,
  setValue
} from '@btime/state-monad'
import { assoc, compose, equals, map, path, pathOr, prop, uniq } from 'ramda'
import { initAndLast, uniqId } from '@btime/helpers'

import { Reducer } from '../../../../utils/reducer/chain'
import {
  alreadyInHistory,
  cleanElement,
  countSelectedQuestionHierarchy,
  countTotalChildren,
  getPathWithReference,
  historyLimit,
  validateAction
} from './reducerHelpers'
import { handleState } from '../../../../utils/reducer/basicStateHandler'
import { DEFAULT_MAX_QUESTIONS } from '../_constants/questions'

const initialQuestionState = {
  name: '',
  questionType: {},
  data: {},
  reference: uniqId(),
  required: false,
  allowNull: false,
  notesEnabled: false,
  notesRequired: false,
  photosEnabled: false,
  photosRequired: false,
  private: false,
  photosFromGallery: false,
  logic: false,
  childDisplayConditions: false,
  children: [],
  displayConditions: undefined
}

const initialCategoryState = {
  children: [],
  selectedCategory: {},
  reference: uniqId()
}

export const initialState = {
  categories: [initialCategoryState],
  name: '',
  type: {},
  usages: [],
  selectedQuestionPath: [],
  conditionsModalOpen: false,
  selectedConditionsIndex: null,
  triggersQuestionCheckModalOpen: false,
  triggersCategoryCheckModalOpen: false,
  selectedCategoryIndex: null,
  history: [],
  maxQuestions: DEFAULT_MAX_QUESTIONS,
  configuration: {
    enabledSegwareTestMode: false,
    segwarePermittedExecutionTime: null
  }
}

export const SET_VALUE = 'SET_VALUE'
export const SELECT_QUESTION = 'SELECT_QUESTION'
export const SET_SELECTED_QUESTION_VALUE = 'SET_SELECTED_QUESTION_VALUE'
export const DUPLICATE_SELECTED_QUESTION = 'DUPLICATE_SELECTED_QUESTION'
export const REMOVE_SELECTED_QUESTION = 'REMOVE_SELECTED_QUESTION'
export const ADD_CHILD_CONDITIONS_ON_SELECTED_QUESTION = 'ADD_CHILD_CONDITIONS_ON_SELECTED_QUESTION'
export const EDIT_CHILD_CONDITION_ON_SELECTED_QUESTION = 'EDIT_CHILD_CONDITION_ON_SELECTED_QUESTION'
export const REMOVE_CHILD_CONDITION_ON_SELECTED_QUESTION = 'REMOVE_CHILD_CONDITION_ON_SELECTED_QUESTION'
export const DUPLICATE_CATEGORY = 'DUPLICATE_CATEGORY'
export const REMOVE_CATEGORY = 'REMOVE_CATEGORY'
export const ADD_CATEGORY = 'ADD_CATEGORY'
export const SET_SELECTED_CATEGORY = 'SET_SELECTED_CATEGORY'
export const ADD_QUESTION_TO_PARENT = 'ADD_QUESTION_TO_PARENT'
export const ADD_QUESTION_CLOSE_TO = 'ADD_QUESTION_CLOSE_TO'
export const MOVE_ELEMENT_CLOSE_TO = 'MOVE_ELEMENT_CLOSE_TO'
export const MOVE_QUESTION_TO_PARENT = 'MOVE_QUESTION_TO_PARENT'
export const SET_CONDITIONS_MODAL_OPEN = 'SET_CONDITIONS_MODAL_OPEN'
export const SET_TRIGGERS_QUESTION_CHECK_MODAL_OPEN = 'SET_TRIGGERS_QUESTION_CHECK_MODAL_OPEN'
export const SET_TRIGGERS_CATEGORY_CHECK_MODAL_OPEN = 'SET_TRIGGERS_CATEGORY_CHECK_MODAL_OPEN'
export const UNDO_VALUE = 'UNDO_VALUE'
export const SET_SELECTED_SEGWARE_TEST_MODE_VALUE = 'SET_SELECTED_SEGWARE_TEST_MODE_VALUE'

const reducer = (state, action) => {
  const actionsToValidate = {
    addAndDuplicateActions: [
      ADD_QUESTION_CLOSE_TO,
      ADD_QUESTION_TO_PARENT,
      DUPLICATE_SELECTED_QUESTION,
      DUPLICATE_CATEGORY
    ],
    duplicateSelectedQuestionAction: DUPLICATE_SELECTED_QUESTION,
    duplicateCategoryAction: DUPLICATE_CATEGORY
  }

  const categoryQuestionsCount = [DUPLICATE_CATEGORY, REMOVE_CATEGORY].includes(action.type)
    ? countTotalChildren([state.categories[action.payload]])
    : undefined

  const dupQuestionsCount =
    DUPLICATE_SELECTED_QUESTION === action.type ? countSelectedQuestionHierarchy(state) : undefined

  const { blocked, message } = validateAction(state, action.type, actionsToValidate, {
    dupQuestionsCount,
    categoryQuestionsCount
  })

  if (blocked && !state.byPassLimit) {
    alert(message)
    return state
  }

  return Reducer(state, action)
    .map(
      handleState(UNDO_VALUE, (state, action) => {
        return State.of({ ...state, touched: true })
          .map(setValue(state.history[state.history.length - 1]))
          .map(moveBack())
          .map(moveTo('history'))
          .map(setValue(state.history))
          .map(removeValueWithIndex(state.history.length - 1))
          .fold()
      })
    )

    .map(
      handleState(SET_VALUE, (state, action) => {
        const { field, value } = action.payload

        return State.of({ ...state, touched: true })
          .map(moveTo(field))
          .map(setValue(value))

          .map(moveBack())
          .map(moveTo('history'))
          .map(setValue([]))
          .fold()
      })
    )

    .map(
      handleState(SELECT_QUESTION, (state, action) => {
        const reference = action.payload
        const fullPath = getPathWithReference(reference, state.categories)

        const { history, conditionsModalOpen, selectedConditionsIndex, ...restOfState } = state
        const isAlreadyInHistory = alreadyInHistory(state, restOfState)

        if (isAlreadyInHistory) {
          return State.of({ ...state, touched: true })
            .map(moveTo('selectedQuestionPath'))
            .map(setValue(fullPath))
            .fold()
        }

        return State.of({ ...state, touched: true })
          .map(moveTo('selectedQuestionPath'))
          .map(setValue(fullPath))

          .map(moveToRoot())
          .map(moveTo('history'))
          .map(setValue(historyLimit(state)))

          .fold()
      })
    )

    .map(
      handleState(SET_SELECTED_QUESTION_VALUE, (state, action) => {
        const { path, value } = action.payload
        const fullPath = state.selectedQuestionPath

        const { history, conditionsModalOpen, selectedConditionsIndex, ...restOfState } = state
        const isAlreadyInHistory = alreadyInHistory(state, restOfState)

        if (isAlreadyInHistory) {
          return State.of({ ...state, touched: true })
            .map(moveToPath(fullPath))
            .map(moveToPath(path))
            .map(setValue(value))
            .fold()
        }

        return State.of({ ...state, touched: true })
          .map(moveToPath(fullPath))
          .map(moveToPath(path))
          .map(setValue(value))

          .map(moveToRoot())
          .map(moveTo('history'))
          .map(setValue(historyLimit(state)))
          .fold()
      })
    )

    .map(
      handleState(SET_SELECTED_SEGWARE_TEST_MODE_VALUE, (state, action) => {
        const { field, value } = action.payload

        return State.of({ ...state, touched: true })
          .map(moveTo('configuration'))
          .map(moveTo(field))
          .map(setValue(value))

          .map(moveBack())
          .map(moveBack())
          .map(moveTo('history'))
          .map(setValue([]))
          .fold()
      })
    )

    .map(
      handleState(DUPLICATE_SELECTED_QUESTION, (state, action) => {
        const [pathWithoutIndex, index] = initAndLast(state.selectedQuestionPath)

        return State.of({ ...state, touched: true })
          .map(moveTo('selectedQuestionPath'))
          .map(setValue([...pathWithoutIndex, index + 1]))

          .map(moveToRoot())
          .map(moveToPath(pathWithoutIndex))
          .map(duplicateValueWithIndex(index))
          .map(moveTo(index + 1))
          .map(setValue(cleanElement))

          .map(moveToRoot())
          .map(moveTo('history'))
          .map(setValue(historyLimit(state)))

          .map(moveToRoot())
          .map(moveTo('totalQuestions'))
          .map(setValue(state.totalQuestions + dupQuestionsCount))
          .fold()
      })
    )

    .map(
      handleState(REMOVE_SELECTED_QUESTION, (state, action) => {
        const [pathWithoutIndex, index] = initAndLast(state.selectedQuestionPath)

        return State.of({ ...state, touched: true })
          .map(moveTo('selectedQuestionPath'))
          .map(setValue([]))

          .map(moveToRoot())
          .map(moveToPath(pathWithoutIndex))
          .map(removeValueWithIndex(index))

          .map(moveToRoot())
          .map(moveTo('history'))
          .map(setValue(historyLimit(state)))

          .map(moveToRoot())
          .map(moveTo('totalQuestions'))
          .map(setValue(state.totalQuestions - countSelectedQuestionHierarchy(state)))
          .fold()
      })
    )

    .map(
      handleState(DUPLICATE_CATEGORY, (state, action) => {
        const index = action.payload

        return State.of({ ...state, touched: true })
          .map(moveTo('categories'))
          .map(duplicateValueWithIndex(index))
          .map(moveTo(index + 1))
          .map(setValue(cleanElement))

          .map(moveToRoot())
          .map(moveTo('history'))
          .map(setValue(historyLimit(state)))

          .map(moveToRoot())
          .map(moveTo('totalQuestions'))
          .map(setValue(state.totalQuestions + categoryQuestionsCount))
          .fold()
      })
    )

    .map(
      handleState(REMOVE_CATEGORY, (state, action) => {
        const index = action.payload

        return State.of({ ...state, touched: true })
          .map(moveTo('selectedQuestionPath'))
          .map(setValue([]))

          .map(moveToRoot())
          .map(moveTo('categories'))
          .map(removeValueWithIndex(index))

          .map(moveToRoot())
          .map(moveTo('history'))
          .map(setValue(historyLimit(state)))

          .map(moveToRoot())
          .map(moveTo('totalQuestions'))
          .map(setValue(state.totalQuestions - categoryQuestionsCount))
          .fold()
      })
    )

    .map(
      handleState(ADD_CATEGORY, (state, action) => {
        return State.of({ ...state, touched: true })
          .map(moveTo('categories'))
          .map(appendValue(cleanElement(initialCategoryState)))

          .map(moveToRoot())
          .map(moveTo('history'))
          .map(setValue(historyLimit(state)))
          .fold()
      })
    )

    .map(
      handleState(SET_SELECTED_CATEGORY, (state, action) => {
        const { index, value } = action.payload

        return State.of({ ...state, touched: true })
          .map(moveTo('categories'))
          .map(moveTo(index))
          .map(moveTo('selectedCategory'))
          .map(setValue(value || {}))

          .map(moveToRoot())
          .map(moveTo('history'))
          .map(setValue(historyLimit(state)))
          .fold()
      })
    )

    .map(
      handleState(ADD_QUESTION_TO_PARENT, (state, action) => {
        let { parentReference, question } = action.payload
        const fullPath = getPathWithReference(parentReference, state.categories)

        return State.of({ ...state, touched: true })
          .map(moveToPath([...fullPath, 'children']))
          .map(appendValue(cleanElement({ ...initialQuestionState, ...question })))

          .map(moveToRoot())
          .map(moveTo('selectedQuestionPath'))
          .map(setValue([...fullPath, 'children', 0]))

          .map(moveToRoot())
          .map(moveTo('history'))
          .map(moveToRoot())
          .map(moveTo('totalQuestions'))
          .map(setValue(state.totalQuestions + 1))
          .fold()
      })
    )

    .map(
      handleState(ADD_QUESTION_CLOSE_TO, (state, action) => {
        let { toReference, question, direction } = action.payload
        let [toPath, toIndex] = initAndLast(getPathWithReference(toReference, state.categories))

        const displayConditions = compose(prop('displayConditions'), path([...toPath, toIndex]))(state)

        if (direction === DIRECTIONS.DOWN) {
          toIndex += 1
        }

        return State.of({ ...state, touched: true })
          .map(moveTo('selectedQuestionPath'))
          .map(setValue([]))

          .map(moveToRoot())
          .map(moveToPath(toPath))
          .map(appendValueOnIndex(toIndex, cleanElement({ ...initialQuestionState, ...question, displayConditions })))

          .map(moveToRoot())
          .map(moveTo('selectedQuestionPath'))
          .map(setValue([...toPath, toIndex]))

          .map(moveToRoot())
          .map(moveTo('history'))
          .map(setValue(historyLimit(state)))
          .map(moveToRoot())
          .map(moveTo('totalQuestions'))
          .map(setValue(state.totalQuestions + 1))
          .fold()
      })
    )

    .map(
      handleState(MOVE_ELEMENT_CLOSE_TO, (state, action) => {
        const { fromReference, toReference, direction } = action.payload
        if (fromReference === toReference) return state

        const [fromPath, fromIndex] = initAndLast(getPathWithReference(fromReference, state.categories))
        const fromQuestion = path([...fromPath, fromIndex], state)

        const stateWithoutFrom = State.of({ ...state, touched: true })
          .map(moveToPath(fromPath))
          .map(removeValueWithIndex(fromIndex))
          .fold()

        let [toPath, toIndex] = initAndLast(getPathWithReference(toReference, stateWithoutFrom.categories))

        const displayConditions = compose(prop('displayConditions'), path([...toPath, toIndex]))(stateWithoutFrom)

        if (direction === DIRECTIONS.DOWN) {
          toIndex += 1
        }

        return State.of(stateWithoutFrom)
          .map(moveTo('selectedQuestionPath'))
          .map(setValue([]))

          .map(moveToRoot())
          .map(moveToPath(toPath))
          .map(appendValueOnIndex(toIndex, { ...fromQuestion, displayConditions }))

          .map(moveToRoot())
          .map(moveTo('history'))
          .map(setValue(historyLimit(state)))
          .fold()
      })
    )

    .map(
      handleState(MOVE_QUESTION_TO_PARENT, (state, action) => {
        const { fromReference, parentReference, displayConditions } = action.payload
        const [fromPath, fromIndex] = initAndLast(getPathWithReference(fromReference, state.categories))

        const fromQuestion = path([...fromPath, fromIndex], state)

        const stateWithoutFrom = State.of({ ...state, touched: true })
          .map(moveToPath(fromPath))
          .map(removeValueWithIndex(fromIndex))
          .fold()

        const parentPath = getPathWithReference(parentReference, stateWithoutFrom.categories)

        return State.of(stateWithoutFrom)
          .map(moveTo('selectedQuestionPath'))
          .map(setValue([]))

          .map(moveToRoot())
          .map(moveToPath(parentPath))
          .map(moveTo('children'))
          .map(appendValue({ ...fromQuestion, displayConditions }))

          .map(moveToRoot())
          .map(moveTo('history'))
          .map(setValue(historyLimit(state)))
          .fold()
      })
    )

    .map(
      handleState(ADD_CHILD_CONDITIONS_ON_SELECTED_QUESTION, (state, action) => {
        const conditions = action.payload
        const fullPath = state.selectedQuestionPath

        return State.of({ ...state, touched: true })
          .map(moveToPath(fullPath))
          .map(moveTo('childDisplayConditions'))
          .map(setValue(value => uniq([...(value || []), ...conditions])))

          .map(moveToRoot())
          .map(moveTo('history'))
          .map(setValue(historyLimit(state)))
          .fold()
      })
    )

    .map(
      handleState(EDIT_CHILD_CONDITION_ON_SELECTED_QUESTION, (state, action) => {
        const conditions = action.payload
        const { selectedConditionsIndex, selectedQuestionPath } = state

        const selectedQuestion = pathOr({}, selectedQuestionPath, state)
        const selectedConditions = pathOr({}, ['childDisplayConditions', selectedConditionsIndex], selectedQuestion)

        return State.of({ ...state, touched: true })
          .map(moveToPath(selectedQuestionPath))
          .map(moveTo('childDisplayConditions'))
          .map(
            setValue(value =>
              uniq((value || []).map((item, index) => (index === selectedConditionsIndex ? conditions : item)))
            )
          )
          .map(moveBack())
          .map(moveTo('children'))
          .map(
            setValue(
              map(question => {
                if (equals(question.displayConditions, selectedConditions)) {
                  return assoc('displayConditions', conditions, question)
                }
                return question
              })
            )
          )

          .map(moveToRoot())
          .map(moveTo('history'))
          .map(setValue(historyLimit(state)))
          .fold()
      })
    )

    .map(
      handleState(REMOVE_CHILD_CONDITION_ON_SELECTED_QUESTION, (state, action) => {
        const { childDisplayConditions, selectedQuestionPath } = state

        const selectedQuestion = pathOr({}, selectedQuestionPath, state)
        const selectedConditions = pathOr({}, ['childDisplayConditions', action.payload], selectedQuestion)

        return State.of({ ...state, touched: true })
          .map(moveToPath(selectedQuestionPath))

          .map(moveTo('childDisplayConditions'))
          .map(removeValueWithIndex(action.payload))
          .map(moveBack())
          .map(moveTo('children'))
          .map(removeValueWith(question => equals(question.displayConditions, selectedConditions)))

          .map(moveToRoot())
          .map(moveTo('history'))
          .map(setValue(historyLimit(state)))
          .fold()
      })
    )

    .map(
      handleState(SET_CONDITIONS_MODAL_OPEN, (state, action) => {
        const { open, index } = action.payload
        return State.of(state)
          .map(moveTo('conditionsModalOpen'))
          .map(setValue(open))
          .map(moveToRoot())
          .map(moveTo('selectedConditionsIndex'))
          .map(setValue(index))
          .fold()
      })
    )

    .map(
      handleState(SET_TRIGGERS_QUESTION_CHECK_MODAL_OPEN, (state, action) => {
        const { open } = action.payload
        return State.of(state) //
          .map(moveTo('triggersQuestionCheckModalOpen'))
          .map(setValue(open))
          .fold()
      })
    )

    .map(
      handleState(SET_TRIGGERS_CATEGORY_CHECK_MODAL_OPEN, (state, action) => {
        const { open, index } = action.payload
        return State.of(state) //
          .map(moveTo('triggersCategoryCheckModalOpen'))
          .map(setValue(open))
          .map(moveToRoot())
          .map(moveTo('selectedCategoryIndex'))
          .map(setValue(index))
          .fold()
      })
    )

    .foldState()
}

export default reducer
